import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { MapPin, BadgeCheck, Phone, MessageCircle, Calendar } from "lucide-react";
import { toast } from "sonner";
import { API, imgUrl, useAuthedApi, errMsg } from "../lib/api";
import DealerLogo from "../components/DealerLogo";
import CarCard from "../components/CarCard";
import BackButton from "../components/BackButton";

export default function DealerStore() {
  const { id } = useParams();
  const { isSignedIn } = useAuth();
  const api = useAuthedApi();
  const navigate = useNavigate();
  const [dealer, setDealer] = useState(null);
  const [cars, setCars] = useState([]);
  const [phone, setPhone] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    axios.get(`${API}/dealers/${id}`).then((r) => setDealer(r.data)).catch(() => setMissing(true));
    axios.get(`${API}/listings`, { params: { dealer_id: id, limit: 48 } }).then((r) => setCars(r.data.items)).catch(() => setCars([]));
  }, [id]);

  const reveal = async (wa) => {
    if (!isSignedIn) return navigate(`/sign-in?redirect_url=${encodeURIComponent(window.location.href)}`);
    try {
      const p = phone || (await api("get", `/dealers/${id}/contact`)).phone;
      setPhone(p);
      if (wa) window.open(`https://wa.me/91${p.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hi ${dealer.name}, I found your store on carsalemarket.com`)}`, "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (missing) return <div className="pt-40 text-center text-gray-400" data-testid="dealer-not-found">This dealer store is not available. <Link to="/dealers" className="text-[#E5C158]">See all dealers</Link></div>;
  if (!dealer) return <div className="pt-40 text-center text-gray-500">Loading...</div>;
  return (
    <div data-testid="dealer-store-page">
      <section className="relative h-[380px] overflow-hidden grain">
        {dealer.banner_url && <img src={imgUrl(dealer.banner_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#08090C] via-[#08090C]/70 to-[#08090C]/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28"><BackButton className="bg-black/50" /></div>
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-28 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end gap-6 justify-between">
          <div className="flex items-end gap-6">
            <DealerLogo dealer={dealer} size="w-28 h-28" text="text-4xl" />
            <div className="space-y-2">
              <p className="eyebrow">Pre-owned dealer store {dealer.is_demo && "· Demo"}</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl flex items-center gap-3" data-testid="dealer-store-name">{dealer.name}{dealer.verified && <BadgeCheck className="w-8 h-8 text-[#D4AF37]" />}</h1>
              <p className="text-sm text-gray-400 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#D4AF37]" />{dealer.address}, {dealer.city}, {dealer.state}</p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            {phone ? <a href={`tel:${phone}`} className="btn-outline" data-testid="dealer-phone-link"><Phone className="w-4 h-4" />{phone}</a>
              : <button className="btn-outline" onClick={() => reveal(false)} data-testid="dealer-call-button"><Phone className="w-4 h-4" />{isSignedIn ? "Show number" : "Sign in to call"}</button>}
            <button className="px-5 py-3 rounded-full bg-[#25D366] text-black font-semibold flex items-center gap-2" onClick={() => reveal(true)} data-testid="dealer-whatsapp-button"><MessageCircle className="w-4 h-4" />WhatsApp</button>
          </div>
        </div>
        <div className="grid md:grid-cols-[1fr_260px] gap-6 mt-10">
          <div className="lux-card p-6"><p className="eyebrow mb-3">About the store</p><p className="text-gray-300 text-sm leading-relaxed">{dealer.about || "No description yet."}</p></div>
          <div className="lux-card p-6 space-y-4">
            <div><p className="font-display text-4xl gold-text" data-testid="dealer-inventory-count">{cars.length}</p><p className="text-xs uppercase tracking-widest text-gray-500">Cars in stock</p></div>
            {dealer.established && <p className="text-sm text-gray-400 flex items-center gap-2"><Calendar className="w-4 h-4 text-[#D4AF37]" />Since {dealer.established}</p>}
          </div>
        </div>
        <h2 className="font-display text-4xl mt-16 mb-8">Inventory</h2>
        {!cars.length && <div className="lux-card p-16 text-center text-gray-400">No cars listed yet.</div>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{cars.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}</div>
      </div>
    </div>
  );
}
